import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Lenis from "lenis";
import {
    motion,
    useMotionValue,
    useSpring,
    useTransform,
} from "framer-motion";
import { toast } from "sonner";
import {
    Package,
    CurrencyDollar,
    Scales,
    Receipt,
    ArrowRight,
    Airplane,
    Truck,
    Sparkle,
    Link as LinkIcon,
    MagnifyingGlass,
    X,
    WhatsappLogo,
    BookmarkSimple,
    Path,
    MapPin,
    Clock,
    CheckCircle,
    WarningCircle,
    Gift,
    Share,
    Copy,
    Lock,
    Camera,
    Upload,
    PencilSimple,
    Plus,
    Minus,
} from "@phosphor-icons/react";
import { computeBreakdown, fmtUsd, fmtGyd } from "../lib/calc";
import { extractPhone, GIVEAWAY_MESSAGE, whatsappShareUrl } from "../lib/phone";
import { parseVideoUrl } from "../lib/video";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labelCls =
    "block uppercase tracking-[0.18em] text-[11px] font-bold text-zinc-500 mb-2";

const inputCls =
    "w-full bg-white border-2 border-zinc-900 px-4 py-4 text-2xl font-display font-bold tracking-tight text-zinc-900 focus:outline-none focus:ring-0 focus:border-zinc-900 focus:shadow-brutal-sm transition-all rounded-none placeholder:text-zinc-300";

function StatPill({ label, value, testId }) {
    return (
        <div className="border-2 border-zinc-900 bg-white px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                {label}
            </div>
            <div
                className="font-mono-tab text-base font-semibold text-zinc-900"
                data-testid={testId}
            >
                {value}
            </div>
        </div>
    );
}

function ReceiptRow({ label, value, sub, testId, accent = false }) {
    return (
        <div className="flex items-start justify-between gap-4 py-3 border-b border-dashed border-zinc-300 last:border-b-0">
            <div className="flex-1">
                <div
                    className={`uppercase tracking-[0.14em] ${
                        accent ? "text-zinc-900 font-bold" : "text-zinc-600"
                    } text-[11px]`}
                >
                    {label}
                </div>
                {sub && (
                    <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>
                )}
            </div>
            <div
                className={`font-mono-tab tabular-nums whitespace-nowrap ${
                    accent ? "text-lg font-bold text-zinc-900" : "text-sm font-medium text-zinc-900"
                }`}
                data-testid={testId}
            >
                {value}
            </div>
        </div>
    );
}

/* ---------- Motion primitives ---------- */
const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0, className = "" }) {
    return (
        <span className={`block overflow-hidden pb-1 ${className}`}>
            <motion.span
                className="block will-change-transform"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function Reveal({ children, delay = 0, className = "", y = 32 }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, delay, ease: EASE }}
        >
            {children}
        </motion.div>
    );
}

const TICKER_ITEMS = [
    "Live rate · USD → GYD",
    "Miami → Georgetown air freight",
    "Tiered shipping by the pound",
    "65% down · balance on delivery",
    "AI price + weight scan",
    "700+ carriers tracked",
];

function Marquee() {
    const Row = () => (
        <div className="flex shrink-0 items-center">
            {TICKER_ITEMS.map((t, i) => (
                <span
                    key={i}
                    className="flex items-center text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-900"
                >
                    <span className="px-6">{t}</span>
                    <Sparkle size={11} weight="fill" />
                </span>
            ))}
        </div>
    );
    return (
        <div
            className="overflow-hidden border-b-2 border-zinc-900 bg-[#FFCC00] py-2.5"
            data-testid="ticker-marquee"
            aria-hidden="true"
        >
            <div className="flex w-max animate-marquee">
                <Row />
                <Row />
            </div>
        </div>
    );
}

function TiltCard({ children, className = "" }) {
    const ref = useRef(null);
    const mx = useMotionValue(0.5);
    const my = useMotionValue(0.5);
    const rotateX = useSpring(useTransform(my, [0, 1], [6, -6]), {
        stiffness: 140,
        damping: 18,
    });
    const rotateY = useSpring(useTransform(mx, [0, 1], [-8, 8]), {
        stiffness: 140,
        damping: 18,
    });
    return (
        <motion.div
            ref={ref}
            onMouseMove={(e) => {
                const r = ref.current?.getBoundingClientRect();
                if (!r) return;
                mx.set((e.clientX - r.left) / r.width);
                my.set((e.clientY - r.top) / r.height);
            }}
            onMouseLeave={() => {
                mx.set(0.5);
                my.set(0.5);
            }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

function ChapterTag({ n, label, dark = false }) {
    return (
        <div className="mb-10 flex items-center gap-4" data-testid={`chapter-${n}`}>
            <span
                className={`font-mono-tab text-[11px] font-bold tracking-[0.3em] ${
                    dark ? "text-[#FFCC00]" : "text-zinc-900"
                }`}
            >
                CH·{n}
            </span>
            <span className={`h-0.5 flex-1 ${dark ? "bg-[#FFCC00]/40" : "bg-zinc-900"}`} />
            <span
                className={`text-[10px] uppercase tracking-[0.3em] font-bold ${
                    dark ? "text-zinc-400" : "text-zinc-500"
                }`}
            >
                {label}
            </span>
        </div>
    );
}

/* ---------- Full-screen image lightbox with zoom ---------- */
function ImageLightbox({ src, onClose }) {
    const [zoom, setZoom] = useState(1);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const clampZoom = (z) => Math.min(4, Math.max(1, Math.round(z * 10) / 10));

    return (
        <div
            className="fixed inset-0 z-[70] flex flex-col bg-zinc-950/95"
            data-lenis-prevent
            data-testid="image-lightbox"
            onClick={onClose}
        >
            {/* Toolbar */}
            <div
                className="flex items-center justify-between gap-3 border-b-2 border-[#FFCC00] bg-zinc-950 px-4 py-3"
                onClick={(e) => e.stopPropagation()}
            >
                <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#FFCC00]">
                    Product image · {Math.round(zoom * 100)}%
                </span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        data-testid="lightbox-zoom-out"
                        aria-label="Zoom out"
                        disabled={zoom <= 1}
                        onClick={() => setZoom((z) => clampZoom(z - 0.5))}
                        className="w-9 h-9 flex items-center justify-center border-2 border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Minus size={16} weight="bold" />
                    </button>
                    <button
                        type="button"
                        data-testid="lightbox-zoom-in"
                        aria-label="Zoom in"
                        disabled={zoom >= 4}
                        onClick={() => setZoom((z) => clampZoom(z + 0.5))}
                        className="w-9 h-9 flex items-center justify-center border-2 border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Plus size={16} weight="bold" />
                    </button>
                    <button
                        type="button"
                        data-testid="lightbox-close"
                        aria-label="Close image and return to quote"
                        onClick={onClose}
                        className="w-9 h-9 flex items-center justify-center border-2 border-zinc-900 bg-[#FFCC00] text-zinc-900 hover:bg-white transition-colors"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>
            </div>

            {/* Image stage — scroll to pan when zoomed */}
            <div className="flex-1 overflow-auto flex" onClick={onClose}>
                <img
                    src={src}
                    alt="Product preview — enlarged"
                    data-testid="lightbox-image"
                    onClick={(e) => {
                        e.stopPropagation();
                        setZoom((z) => (z > 1 ? 1 : 2));
                    }}
                    className={`m-auto bg-white ${
                        zoom === 1 ? "cursor-zoom-in" : "cursor-zoom-out"
                    }`}
                    style={
                        zoom === 1
                            ? { maxWidth: "92vw", maxHeight: "78vh", objectFit: "contain" }
                            : { width: `${zoom * 88}vw`, maxWidth: "none", maxHeight: "none" }
                    }
                />
            </div>
            <div
                className="border-t-2 border-[#FFCC00] bg-zinc-950 px-4 py-2 text-center text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500"
                onClick={(e) => e.stopPropagation()}
            >
                Tap image to zoom · press Esc or X to return to your quote
            </div>
        </div>
    );
}

export default function CalculatorPage() {
    const [usd, setUsd] = useState("100");
    const [taxPercent, setTaxPercent] = useState("7");
    const [pounds, setPounds] = useState("1");

    // Smooth momentum scrolling (Lenis) — handles anchor links too
    useEffect(() => {
        const lenis = new Lenis({ autoRaf: true, anchors: true, lerp: 0.09 });
        return () => lenis.destroy();
    }, []);

    // URL scanner state
    const [scanUrl, setScanUrl] = useState("");
    const [scanLoading, setScanLoading] = useState(false);
    const [scanError, setScanError] = useState("");
    const [scanResult, setScanResult] = useState(null);

    const handleScan = async () => {
        const trimmed = scanUrl.trim();
        if (!trimmed) {
            setScanError("Paste a product link first.");
            return;
        }
        if (!/^https?:\/\//i.test(trimmed)) {
            setScanError("Link must start with http:// or https://");
            return;
        }
        setScanLoading(true);
        setScanError("");
        setScanResult(null);
        try {
            const { data } = await axios.post(
                `${API}/scan-price`,
                { url: trimmed },
                { timeout: 120000 }
            );
            setScanResult(data);
            if (data.price_usd) {
                setUsd(String(data.price_usd));
            }
            if (data.billable_pounds) {
                setPounds(String(data.billable_pounds));
            }
            if (!data.price_usd && data.note) {
                setScanError(data.note);
            }
        } catch (err) {
            const msg =
                err.response?.data?.detail ||
                err.message ||
                "Could not scan that link.";
            setScanError(typeof msg === "string" ? msg : "Could not scan that link.");
        } finally {
            setScanLoading(false);
        }
    };

    const clearScan = () => {
        setScanUrl("");
        setScanResult(null);
        setScanError("");
    };

    const result = useMemo(
        () =>
            computeBreakdown({
                usd,
                taxPercent,
                pounds,
            }),
        [usd, taxPercent, pounds]
    );

    const handleReset = () => {
        setUsd("");
        setTaxPercent("7");
        setPounds("");
    };

    // ---------- Quote save / load ----------
    const [savedQuoteId, setSavedQuoteId] = useState("");

    const saveQuote = useCallback(async () => {
        try {
            const payload = {
                usd: String(usd || ""),
                tax_percent: String(taxPercent || "7"),
                pounds: String(pounds || ""),
                scan: scanResult
                    ? {
                          title: scanResult.title || null,
                          image_url: scanResult.image_url || null,
                          source_url: scanResult.source_url || null,
                          price_usd: scanResult.price_usd || null,
                          billable_pounds: scanResult.billable_pounds || null,
                          weight_pounds: scanResult.weight_pounds || null,
                      }
                    : null,
            };
            const { data } = await axios.post(`${API}/quotes`, payload, {
                timeout: 15000,
            });
            return data.id;
        } catch (e) {
            return null;
        }
    }, [usd, taxPercent, pounds, scanResult]);

    const handleSaveQuote = async () => {
        const tId = toast.loading("Saving quote…");
        const id = await saveQuote();
        toast.dismiss(tId);
        if (!id) {
            toast.error("Could not save quote. Try again.");
            return;
        }
        setSavedQuoteId(id);
        const url = `${window.location.origin}/?q=${id}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success(`Quote #${id} saved — link copied!`, {
                description: url,
                duration: 8000,
            });
        } catch {
            toast.success(`Quote #${id} saved`, {
                description: url,
                duration: 8000,
            });
        }
    };

    // Load quote from URL ?q=ID on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const qid = params.get("q");
        if (!qid) return;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API}/quotes/${encodeURIComponent(qid)}`,
                    { timeout: 15000 }
                );
                if (data.usd) setUsd(String(data.usd));
                if (data.tax_percent) setTaxPercent(String(data.tax_percent));
                if (data.pounds) setPounds(String(data.pounds));
                if (data.scan) {
                    setScanResult({
                        title: data.scan.title,
                        image_url: data.scan.image_url,
                        source_url: data.scan.source_url,
                        price_usd: data.scan.price_usd,
                        billable_pounds: data.scan.billable_pounds,
                        weight_pounds: data.scan.weight_pounds,
                    });
                    if (data.scan.source_url) setScanUrl(data.scan.source_url);
                }
                setSavedQuoteId(data.id);
                toast.success(`Loaded quote #${data.id}`);
            } catch (e) {
                toast.error("Quote not found.");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // WhatsApp quote sender — ALWAYS saves a brand-new quote (new ID) with the
    // current figures, so the re-open link in the message matches exactly what
    // was sent. Reusing an old ID would make the link show stale numbers.
    const handleWhatsApp = async () => {
        const saveId = await saveQuote();
        if (saveId) setSavedQuoteId(saveId);

        const quoteId = saveId || "DRAFT";
        const reopenUrl = saveId
            ? `${window.location.origin}/?q=${saveId}`
            : null;

        const lines = [
            `*Cargo Quote #${quoteId}*`,
            "",
            "*USA SIDE*",
            `Purchase (USD): ${fmtUsd(result.usdAmount)}`,
            `Sales tax · ${result.taxPercent}%: ${fmtUsd(result.salesTaxUsd)}`,
            `USD total: ${fmtUsd(result.usdWithTax)}`,
            "",
            "*GUYANA SIDE*",
            `Item value: ${fmtGyd(result.itemValueGyd)}`,
            `Sales tax: ${fmtGyd(result.salesTaxGyd)}`,
            `Shipping · ${result.pounds} lb: ${fmtGyd(result.shippingGyd)}`,
            `Subtotal: ${fmtGyd(result.subtotalGyd)}`,
            `Duties / airport / insurance: ${fmtGyd(result.feesGyd)}`,
            "",
            `*GRAND TOTAL: ${fmtGyd(result.grandTotalGyd)}*`,
            "",
            `*Down payment · 65%: ${fmtGyd(result.downPaymentGyd)}*`,
            `Balance on delivery: ${fmtGyd(result.balanceGyd)}`,
        ];

        if (scanResult?.title) {
            lines.splice(
                1,
                0,
                "",
                `*Item:* ${scanResult.title}`,
                scanResult.source_url ? `*Link:* ${scanResult.source_url}` : null
            );
        }

        if (reopenUrl) {
            lines.push("", `Re-open this quote: ${reopenUrl}`);
        }

        const text = lines.filter(Boolean).join("\n");
        const url = `https://wa.me/5926665489?text=${encodeURIComponent(text)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    // ---------- Tracking portal ----------
    const [trackNum, setTrackNum] = useState("");
    const [trackZip, setTrackZip] = useState("");
    const [trackLoading, setTrackLoading] = useState(false);
    const [trackError, setTrackError] = useState("");
    const [trackResult, setTrackResult] = useState(null);

    // Giveaway modal state
    const [giveaway, setGiveaway] = useState(null); // { phone, total_entries }

    // Full-screen image lightbox
    const [lightboxSrc, setLightboxSrc] = useState(null);

    const needsZip = /^TB[AM]/i.test(trackNum.trim());

    const handleTrack = async () => {
        const t = trackNum.trim();
        if (!t) {
            setTrackError("Enter a tracking number.");
            return;
        }

        // If the input looks like a phone number → giveaway flow
        const phone = extractPhone(t);
        if (phone) {
            setTrackError("");
            setTrackResult(null);
            setTrackLoading(true);
            try {
                const { data } = await axios.post(
                    `${API}/entries`,
                    { phone, source: "tracking" },
                    { timeout: 15000 }
                );
                setGiveaway({
                    phone: data.phone,
                    total_entries: data.total_entries,
                });
                setTrackNum("");
            } catch (err) {
                const msg =
                    err.response?.data?.detail || err.message || "Could not enter you.";
                setTrackError(typeof msg === "string" ? msg : "Entry failed.");
            } finally {
                setTrackLoading(false);
            }
            return;
        }

        if (needsZip && !trackZip.trim()) {
            setTrackError(
                "Amazon shipments need the destination ZIP code (5 digits) to fetch events."
            );
            return;
        }
        setTrackLoading(true);
        setTrackError("");
        setTrackResult(null);
        try {
            const { data } = await axios.post(
                `${API}/track`,
                {
                    tracking_number: t,
                    postal_code: trackZip.trim() || null,
                },
                { timeout: 60000 }
            );
            setTrackResult(data);
            setTimeout(() => {
                const el = document.getElementById("track-results");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 50);
        } catch (err) {
            const msg =
                err.response?.data?.detail ||
                err.message ||
                "Could not retrieve tracking info.";
            setTrackError(typeof msg === "string" ? msg : "Tracking failed.");
        } finally {
            setTrackLoading(false);
        }
    };

    const clearTrack = () => {
        setTrackNum("");
        setTrackZip("");
        setTrackResult(null);
        setTrackError("");
    };

    const copyGiveaway = async () => {
        try {
            await navigator.clipboard.writeText(GIVEAWAY_MESSAGE);
            toast.success("Copied — now paste in your groups!");
        } catch {
            toast.error("Could not copy. Long-press the message below.");
        }
    };

    // ---------- Image scan (screenshot upload) ----------
    const [imgLoading, setImgLoading] = useState(false);
    const [imgReview, setImgReview] = useState(null); // extracted data pending user confirm
    const [imgPreview, setImgPreview] = useState("");

    const onImageChosen = async (file) => {
        if (!file) return;
        if (file.size > 12 * 1024 * 1024) {
            toast.error("Image too large (max 12 MB).");
            return;
        }
        setImgPreview(URL.createObjectURL(file));
        setImgLoading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await axios.post(`${API}/scan-image`, form, {
                timeout: 120000,
            });
            const initialVariant =
                data.variants && data.variants.length > 0
                    ? data.variants[0].label
                    : null;
            setImgReview({
                ...data,
                selected_variant: initialVariant,
                edit_title: data.title || "",
                edit_item_price: data.item_price ?? "",
                edit_seller_shipping: data.seller_shipping ?? "",
                edit_order_total: data.order_total ?? "",
                edit_currency: data.currency || "USD",
                edit_weight_pounds: data.weight_pounds ?? "",
                // Tax rule: order total → 0% (tax inside it); explicit tax % → use it;
                // explicit tax amount → convert to % of item price; otherwise assume 7%.
                edit_tax_percent:
                    data.order_total != null
                        ? "0"
                        : data.sales_tax_percent != null
                        ? String(data.sales_tax_percent)
                        : data.sales_tax != null && data.item_price
                        ? String(
                              Math.round((data.sales_tax / data.item_price) * 10000) / 100
                          )
                        : "7",
            });
        } catch (err) {
            const msg =
                err.response?.data?.detail || err.message || "Could not read image.";
            toast.error(typeof msg === "string" ? msg : "Image scan failed.");
        } finally {
            setImgLoading(false);
        }
    };

    const applyImgReview = () => {
        if (!imgReview) return;
        const ip = Number(imgReview.edit_item_price) || 0;
        const ss = Number(imgReview.edit_seller_shipping) || 0;
        const ot = Number(imgReview.edit_order_total) || 0;
        const cur = imgReview.edit_currency || "USD";
        const rate =
            {
                USD: 1, GBP: 1.27, EUR: 1.08, CAD: 0.74, AUD: 0.66,
                JPY: 0.0067, INR: 0.012, MXN: 0.058, BRL: 0.20, CNY: 0.14,
            }[cur] || 1;
        // A final order/checkout total shown on the screenshot already includes
        // item + shipping + tax + discounts — it takes priority over the parts.
        const purchaseUsd =
            ot > 0
                ? Math.round(ot * rate * 100) / 100
                : Math.round((ip + ss) * rate * 100) / 100;
        if (purchaseUsd > 0) setUsd(String(purchaseUsd));
        if (ot > 0) {
            setTaxPercent("0"); // tax already inside the checkout total
        } else {
            // explicit tax from screenshot wins; otherwise the standing 7% assumption
            const tp = parseFloat(imgReview.edit_tax_percent);
            setTaxPercent(!isNaN(tp) && tp >= 0 ? String(tp) : "7");
        }

        const wp = Number(imgReview.edit_weight_pounds) || 0;
        if (wp > 0) setPounds(String(Math.ceil(wp)));

        setScanResult({
            title: imgReview.edit_title,
            image_url: imgReview.image_url
                ? `${process.env.REACT_APP_BACKEND_URL}${imgReview.image_url}`
                : imgPreview || null,
            source_url: null,
            price_usd: purchaseUsd,
            currency_original: cur,
            price_original: ot > 0 ? ot : ip + ss,
            billable_pounds: wp > 0 ? Math.ceil(wp) : null,
            weight_pounds: wp || null,
            weight_source:
                imgReview.weight_source === "web"
                    ? "web"
                    : imgReview.weight_source === "screenshot"
                    ? "screenshot"
                    : "estimate",
            image_seller_shipping_usd: Math.round(ss * rate * 100) / 100,
            image_item_price_usd: Math.round(ip * rate * 100) / 100,
            image_order_total_usd: ot > 0 ? Math.round(ot * rate * 100) / 100 : null,
        });

        toast.success(
            ot > 0
                ? "Order total applied — tax & shipping already included, sales tax set to 0%."
                : "Details applied — quote updated."
        );
        setImgReview(null);
        setTimeout(() => {
            const el = document.getElementById("calc");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
    };

    // Featured video from admin — fallback to the original TikTok if none set
    const [featuredVideo, setFeaturedVideo] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/settings/video`, {
                    timeout: 8000,
                });
                if (data.url) {
                    const parsed = parseVideoUrl(data.url);
                    if (parsed) setFeaturedVideo(parsed);
                }
            } catch {
                // ignore
            }
        })();
    }, []);

    // Auto-scan when arriving via ?scan=<url> (bookmarklet / Chrome extension)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const s = params.get("scan");
        if (!s) return;
        try {
            const decoded = decodeURIComponent(s);
            if (/^https?:\/\//i.test(decoded)) {
                setScanUrl(decoded);
                setTimeout(() => {
                    handleScan();
                    const el = document.getElementById("calc");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 400);
            }
        } catch {
            // ignore
        }
    }, []);

    return (
        <div className="min-h-screen bg-[#F4F4F5]">
            {/* EDITORIAL TICKER */}
            <Marquee />

            {/* NAV */}
            <header className="sticky top-0 z-40 border-b-2 border-zinc-900 bg-white/95 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center">
                            <Package size={22} weight="bold" color="#FFCC00" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-display text-lg font-extrabold tracking-tight">
                                CARGO/USD→GYD
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                Shipping Calculator
                            </div>
                        </div>
                    </div>
                    <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">
                        <a href="#calc" className="hover:text-zinc-900">
                            Calculator
                        </a>
                        <a href="#track" className="hover:text-zinc-900">
                            Track
                        </a>
                        <a href="#how" className="hover:text-zinc-900">
                            How it works
                        </a>
                    </nav>
                </div>
            </header>

            {/* HERO */}
            <section className="relative overflow-hidden border-b-2 border-zinc-900 bg-zinc-950 bg-grid-dark noise">
                <div className="spotlight pointer-events-none absolute inset-0" />
                <div className="relative max-w-7xl mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        {/* Inline tracking card */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                            className="mb-8 border-2 border-zinc-900 bg-white shadow-brutal-yellow p-4 sm:p-5"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Path size={14} weight="bold" />
                                <span className="text-[11px] uppercase tracking-[0.25em] font-bold">
                                    Track your cargo
                                </span>
                                <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-400">
                                    All carriers
                                </span>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <div className="flex-1 relative">
                                    <input
                                        data-testid="hero-track-input"
                                        type="text"
                                        value={trackNum}
                                        onChange={(e) => setTrackNum(e.target.value)}
                                        placeholder="Enter tracking number (USPS, UPS, FedEx, DHL …)"
                                        className="w-full bg-white border-2 border-zinc-900 px-3 py-3 pr-10 text-sm font-mono-tab text-zinc-900 focus:outline-none focus:shadow-brutal-sm rounded-none placeholder:text-zinc-400"
                                        disabled={trackLoading}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleTrack();
                                            }
                                        }}
                                    />
                                    {trackNum && !trackLoading && (
                                        <button
                                            type="button"
                                            data-testid="hero-track-clear"
                                            onClick={clearTrack}
                                            aria-label="Clear"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center border-2 border-zinc-900 bg-white hover:bg-zinc-900 hover:text-[#FFCC00] transition-colors"
                                        >
                                            <X size={11} weight="bold" />
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    data-testid="hero-track-btn"
                                    onClick={handleTrack}
                                    disabled={trackLoading}
                                    className="border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                >
                                    {trackLoading ? (
                                        <>
                                            <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                            Tracking…
                                        </>
                                    ) : (
                                        <>
                                            <MagnifyingGlass size={14} weight="bold" />
                                            Track
                                        </>
                                    )}
                                </button>
                            </div>
                            {trackError && (
                                <div
                                    data-testid="hero-track-error"
                                    className="mt-2 text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-700"
                                >
                                    {trackError}
                                </div>
                            )}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                            className="inline-flex items-center gap-2 border-2 border-[#FFCC00]/60 text-[#FFCC00] px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold mb-6"
                        >
                            <Airplane size={14} weight="bold" />
                            Miami → Georgetown · Air Freight
                        </motion.div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tighter text-white">
                            <MaskedLine delay={0.25}>Ship to Guyana.</MaskedLine>
                            <MaskedLine delay={0.37}>Know the cost</MaskedLine>
                            <MaskedLine delay={0.49}>
                                <span className="inline-block border-2 border-[#FFCC00] bg-[#FFCC00] px-2 text-zinc-900 shadow-[5px_5px_0px_0px_rgba(255,204,0,0.35)]">
                                    before you pay.
                                </span>
                            </MaskedLine>
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                            className="mt-6 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed"
                        >
                            A pragmatic, no-nonsense calculator that converts your USD purchase
                            into Guyanese Dollars and stacks every fee — sales tax, tiered
                            shipping by pound, duties, airport handling and insurance —
                            into one final figure.
                        </motion.p>

                        {/* Merchant shortcuts */}
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                            className="mt-8"
                        >
                            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-3">
                                Shop from · tap a store to open
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { name: "Amazon", url: "https://www.amazon.com", bg: "#232F3E", fg: "#FF9900" },
                                    { name: "eBay", url: "https://www.ebay.com", bg: "#FFFFFF", fg: "#E53238", border: true },
                                    { name: "Walmart", url: "https://www.walmart.com", bg: "#0071DC", fg: "#FFC220" },
                                    { name: "SHEIN", url: "https://www.shein.com", bg: "#000000", fg: "#FFFFFF" },
                                    { name: "AliExpress", url: "https://www.aliexpress.com", bg: "#E62E04", fg: "#FFFFFF" },
                                ].map((m) => (
                                    <a
                                        key={m.name}
                                        href={m.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`merchant-${m.name.toLowerCase()}`}
                                        className="border-2 border-zinc-900 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.15em] hover:-translate-y-0.5 hover:shadow-brutal-sm transition-all inline-flex items-center gap-2"
                                        style={{ backgroundColor: m.bg, color: m.fg }}
                                    >
                                        {m.name}
                                    </a>
                                ))}
                            </div>
                            <div className="mt-2 text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                                Bookmark our <a href="#bookmarklet" className="underline text-[#FFCC00] hover:text-white">Quick-Scan button</a> for 1-tap price scans.
                            </div>
                        </motion.div>
                    </div>
                    <div className="md:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, y: 48, rotate: 2 }}
                            animate={{ opacity: 1, y: 0, rotate: 0 }}
                            transition={{ duration: 1, delay: 0.55, ease: EASE }}
                            style={{ perspective: 1100 }}
                        >
                            <TiltCard className="border-2 border-[#FFCC00] bg-white shadow-brutal-yellow overflow-hidden">
                            <div className="bg-zinc-900 text-white px-4 py-2 flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-[0.25em] font-bold">
                                    {featuredVideo?.platform === "youtube"
                                        ? "YouTube"
                                        : featuredVideo?.platform === "facebook"
                                        ? "Facebook"
                                        : "@matrixshopping"}
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFCC00]">
                                    ▶ Watch
                                </span>
                            </div>
                            <div
                                className="relative w-full"
                                style={{
                                    paddingTop:
                                        featuredVideo?.aspect === "16/9" ? "56.25%" : "177%",
                                }}
                            >
                                <iframe
                                    title="Featured video"
                                    src={
                                        featuredVideo?.embedUrl ||
                                        "https://www.tiktok.com/player/v1/7652046449311485205?autoplay=0&music_info=1&description=1"
                                    }
                                    allow="fullscreen; encrypted-media; picture-in-picture"
                                    allowFullScreen
                                    className="absolute inset-0 w-full h-full border-0"
                                />
                            </div>
                            </TiltCard>
                        </motion.div>
                        <div className="mt-3 text-center text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                            Featured · straight from the warehouse floor
                        </div>
                    </div>
                </div>
            </section>

            {/* MAIN CALCULATOR */}
            <section id="calc" className="border-b-2 border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 py-14 md:py-20">
                    <ChapterTag n="01" label="The Calculator" />
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    {/* INPUT FORM */}
                    <Reveal className="md:col-span-7">
                        <div className="bg-white border-2 border-zinc-900 p-6 sm:p-8 shadow-brutal">
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                                        Step 01
                                    </div>
                                    <h2 className="font-display text-3xl font-extrabold tracking-tight">
                                        Build your shipment
                                    </h2>
                                </div>
                                <button
                                    onClick={handleReset}
                                    data-testid="reset-btn"
                                    className="border-2 border-zinc-900 bg-white px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-zinc-900 hover:text-white transition-colors"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {/* IMAGE / SCREENSHOT UPLOAD */}
                                <div className="sm:col-span-2 border-2 border-zinc-900 bg-[#E7F5FF] p-4">
                                    <label className={labelCls}>
                                        <span className="inline-flex items-center gap-2">
                                            <Camera size={12} weight="bold" />
                                            Upload a product screenshot · AI reads price + weight
                                        </span>
                                    </label>
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <label className="cursor-pointer border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors inline-flex items-center gap-2">
                                            <Upload size={14} weight="bold" />
                                            Choose image
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                                                className="hidden"
                                                data-testid="input-scan-image"
                                                onChange={(e) => onImageChosen(e.target.files?.[0])}
                                                disabled={imgLoading}
                                            />
                                        </label>
                                        <label className="cursor-pointer border-2 border-zinc-900 bg-white text-zinc-900 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] transition-colors inline-flex items-center gap-2">
                                            <Camera size={14} weight="bold" />
                                            Take photo
                                            <input
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                className="hidden"
                                                data-testid="input-take-photo"
                                                onChange={(e) => onImageChosen(e.target.files?.[0])}
                                                disabled={imgLoading}
                                            />
                                        </label>
                                        {imgLoading && (
                                            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-700">
                                                <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                Reading image…
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-2 text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                                        JPG · PNG · WEBP · HEIC · max 12 MB
                                    </div>
                                </div>

                                {/* PRODUCT LINK SCANNER */}
                                <div className="sm:col-span-2 border-2 border-zinc-900 bg-[#FFCC00]/30 p-4">
                                    <label className={labelCls} htmlFor="scan-url">
                                        <span className="inline-flex items-center gap-2">
                                            <LinkIcon size={12} weight="bold" />
                                            Paste product link · AI auto-fills price
                                        </span>
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="flex-1 relative">
                                            <input
                                                id="scan-url"
                                                data-testid="input-scan-url"
                                                type="url"
                                                value={scanUrl}
                                                onChange={(e) => setScanUrl(e.target.value)}
                                                placeholder="https://www.amazon.com/... or any product URL"
                                                className="w-full bg-white border-2 border-zinc-900 px-3 py-3 pr-10 text-sm font-mono-tab text-zinc-900 focus:outline-none focus:shadow-brutal-sm rounded-none placeholder:text-zinc-400"
                                                disabled={scanLoading}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleScan();
                                                    }
                                                }}
                                            />
                                            {scanUrl && !scanLoading && (
                                                <button
                                                    type="button"
                                                    data-testid="scan-clear-btn"
                                                    onClick={clearScan}
                                                    aria-label="Clear link"
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center border-2 border-zinc-900 bg-white hover:bg-zinc-900 hover:text-[#FFCC00] transition-colors"
                                                >
                                                    <X size={11} weight="bold" />
                                                </button>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            data-testid="scan-btn"
                                            onClick={handleScan}
                                            disabled={scanLoading}
                                            className="border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                                        >
                                            {scanLoading ? (
                                                <>
                                                    <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                    Scanning…
                                                </>
                                            ) : (
                                                <>
                                                    <MagnifyingGlass size={14} weight="bold" />
                                                    Scan price
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {scanError && (
                                        <div
                                            data-testid="scan-error"
                                            className="mt-3 border-2 border-zinc-900 bg-white px-3 py-2 text-xs font-bold text-zinc-900"
                                        >
                                            {scanError}
                                        </div>
                                    )}

                                    {scanResult && (scanResult.title || scanResult.price_usd || scanResult.image_url) && (
                                        <div
                                            data-testid="scan-result"
                                            className="mt-3 border-2 border-zinc-900 bg-white p-3 flex gap-3 relative"
                                        >
                                            <button
                                                type="button"
                                                onClick={clearScan}
                                                className="absolute top-1 right-1 p-1 hover:bg-zinc-100"
                                                aria-label="Dismiss"
                                            >
                                                <X size={12} weight="bold" />
                                            </button>
                                            {scanResult.image_url ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setLightboxSrc(scanResult.image_url)}
                                                    data-testid="scan-result-image"
                                                    aria-label="Enlarge product image"
                                                    title="Tap to enlarge"
                                                    className="relative group w-20 h-20 flex-shrink-0 border-2 border-zinc-900 bg-zinc-50 cursor-zoom-in overflow-hidden"
                                                >
                                                    <img
                                                        src={scanResult.image_url}
                                                        alt={scanResult.title || "Product"}
                                                        className="w-full h-full object-contain"
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = "none";
                                                        }}
                                                    />
                                                    <span className="absolute inset-0 flex items-center justify-center bg-zinc-900/0 text-transparent group-hover:bg-zinc-900/20 group-hover:text-[#FFCC00] transition-colors">
                                                        <Plus size={22} weight="bold" />
                                                    </span>
                                                </button>
                                            ) : (
                                                <div className="w-20 h-20 border-2 border-zinc-900 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                                                    <Package size={28} weight="bold" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0 pr-4">
                                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                                                    Detected
                                                </div>
                                                <div
                                                    className="text-sm font-bold text-zinc-900 leading-tight line-clamp-2 mt-0.5"
                                                    title={scanResult.title}
                                                >
                                                    {scanResult.title || "Untitled product"}
                                                </div>
                                                {scanResult.price_usd ? (
                                                    <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                                                        <span className="font-display text-xl font-extrabold tracking-tight text-zinc-900">
                                                            {fmtUsd(scanResult.price_usd)}
                                                        </span>
                                                        {scanResult.currency_original &&
                                                            scanResult.currency_original !== "USD" && (
                                                                <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
                                                                    from {scanResult.currency_original}
                                                                    {" "}
                                                                    {Number(scanResult.price_original).toLocaleString()}
                                                                </span>
                                                            )}
                                                    </div>
                                                ) : (
                                                    <div className="mt-1 text-xs text-zinc-500">
                                                        Price not detected — enter manually below.
                                                    </div>
                                                )}
                                                {scanResult.billable_pounds && (
                                                    <div className="mt-1 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-700">
                                                        Weight ·{" "}
                                                        <span className="font-mono-tab">
                                                            {scanResult.weight_pounds} lb
                                                        </span>{" "}
                                                        →{" "}
                                                        <span className="bg-[#FFCC00] px-1 border border-zinc-900 font-mono-tab">
                                                            billed {scanResult.billable_pounds} lb
                                                        </span>
                                                        {(scanResult.weight_source === "estimated" || scanResult.weight_source === "estimate") && (
                                                            <span className="text-zinc-500"> · ai estimate</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* USD */}
                                <div className="sm:col-span-2">
                                    <label className={labelCls} htmlFor="usd">
                                        <span className="inline-flex items-center gap-2">
                                            <CurrencyDollar size={12} weight="bold" />
                                            Purchase amount (USD)
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-display font-bold text-zinc-400">
                                            $
                                        </span>
                                        <input
                                            id="usd"
                                            data-testid="input-usd"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={usd}
                                            onChange={(e) => setUsd(e.target.value)}
                                            placeholder="0.00"
                                            className={inputCls + " pl-10"}
                                        />
                                    </div>
                                </div>

                                {/* TAX % */}
                                <div>
                                    <label className={labelCls} htmlFor="tax">
                                        <span className="inline-flex items-center gap-2">
                                            <Scales size={12} weight="bold" />
                                            USA sales tax %
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="tax"
                                            data-testid="input-tax"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={taxPercent}
                                            onChange={(e) => setTaxPercent(e.target.value)}
                                            placeholder="7"
                                            className={inputCls + " pr-10"}
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl font-display font-bold text-zinc-400">
                                            %
                                        </span>
                                    </div>
                                </div>

                                {/* POUNDS */}
                                <div className="sm:col-span-2">
                                    <label className={labelCls} htmlFor="weight">
                                        <span className="inline-flex items-center gap-2">
                                            <Truck size={12} weight="bold" />
                                            Weight (pounds)
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="weight"
                                            data-testid="input-weight"
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={pounds}
                                            onChange={(e) => setPounds(e.target.value)}
                                            placeholder="0"
                                            className={inputCls + " pr-14"}
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold uppercase tracking-widest text-zinc-400">
                                            lb
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Quick weight chips */}
                            <div className="mt-8">
                                <div className={labelCls}>Quick weight</div>
                                <div className="flex flex-wrap gap-2">
                                    {[1, 2, 3, 4, 5, 6, 7, 10].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            data-testid={`quick-weight-${n}`}
                                            onClick={() => setPounds(String(n))}
                                            className={`border-2 border-zinc-900 px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-all ${
                                                String(n) === String(pounds)
                                                    ? "bg-zinc-900 text-[#FFCC00]"
                                                    : "bg-white text-zinc-900 hover:-translate-y-0.5 hover:shadow-brutal-sm"
                                            }`}
                                        >
                                            {n} lb
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Conversion preview row */}
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="border-2 border-zinc-900 bg-white p-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                                    USD entered
                                </div>
                                <div
                                    className="font-mono-tab text-xl font-bold mt-1"
                                    data-testid="preview-usd"
                                >
                                    {fmtUsd(result.usdAmount)}
                                </div>
                                {scanResult?.image_item_price_usd != null && (
                                    <div className="mt-2 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500 space-y-0.5">
                                        {scanResult.image_order_total_usd != null && (
                                            <div className="text-zinc-900">
                                                Checkout total used · {fmtUsd(scanResult.image_order_total_usd)} · all charges incl.
                                            </div>
                                        )}
                                        <div>Item · {fmtUsd(scanResult.image_item_price_usd)}</div>
                                        <div>
                                            Seller shipping ·{" "}
                                            {fmtUsd(scanResult.image_seller_shipping_usd || 0)}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="border-2 border-zinc-900 bg-white p-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                                    USD + Tax
                                </div>
                                <div
                                    className="font-mono-tab text-xl font-bold mt-1"
                                    data-testid="preview-usd-with-tax"
                                >
                                    {fmtUsd(result.usdWithTax)}
                                </div>
                            </div>
                            <div className="border-2 border-zinc-900 bg-white p-4">
                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                                    Converted GYD
                                </div>
                                <div
                                    className="font-mono-tab text-xl font-bold mt-1 text-zinc-900"
                                    data-testid="preview-converted-gyd"
                                >
                                    {fmtGyd(result.itemValueGyd + result.salesTaxGyd)}
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    {/* RECEIPT */}
                    <Reveal delay={0.12} className="md:col-span-5">
                        <div className="md:sticky md:top-24">
                            <div className="bg-white border-2 border-zinc-900 shadow-brutal">
                                {/* Header */}
                                <div className="bg-zinc-900 text-white px-6 py-4 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Receipt size={18} weight="bold" color="#FFCC00" />
                                        <span className="font-display text-sm font-bold uppercase tracking-[0.25em]">
                                            Cargo Receipt
                                        </span>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-300 font-mono-tab">
                                        #GYD-{String(result.pounds || 0).padStart(2, "0")}
                                        {String(Math.round(result.usdAmount)).slice(-3) || "000"}
                                    </span>
                                </div>

                                <div className="p-6">
                                    {/* USD Section */}
                                    <div className="mb-4">
                                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-400 mb-2">
                                            USA Side
                                        </div>
                                        <ReceiptRow
                                            label="Purchase (USD)"
                                            value={fmtUsd(result.usdAmount)}
                                            testId="receipt-usd"
                                        />
                                        <ReceiptRow
                                            label={`Sales tax · ${result.taxPercent}%`}
                                            value={fmtUsd(result.salesTaxUsd)}
                                            testId="receipt-tax-usd"
                                        />
                                        <ReceiptRow
                                            label="USD Total"
                                            value={fmtUsd(result.usdWithTax)}
                                            testId="receipt-usd-total"
                                            accent
                                        />
                                    </div>

                                    {/* Conversion */}
                                    <div className="my-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-900 bg-[#FFCC00] border-2 border-zinc-900 px-3 py-2">
                                        <span>USD</span>
                                        <ArrowRight size={12} weight="bold" />
                                        <span>GYD</span>
                                    </div>

                                    {/* GYD Section */}
                                    <div className="mb-4">
                                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-400 mb-2">
                                            Guyana Side
                                        </div>
                                        <ReceiptRow
                                            label="Item value (GYD)"
                                            value={fmtGyd(result.itemValueGyd)}
                                            testId="receipt-item-gyd"
                                        />
                                        <ReceiptRow
                                            label="Sales tax (GYD)"
                                            value={fmtGyd(result.salesTaxGyd)}
                                            testId="receipt-tax-gyd"
                                        />
                                        <ReceiptRow
                                            label={`Shipping · ${result.pounds} lb`}
                                            value={fmtGyd(result.shippingGyd)}
                                            testId="receipt-shipping-gyd"
                                        />
                                        <ReceiptRow
                                            label="Subtotal"
                                            value={fmtGyd(result.subtotalGyd)}
                                            testId="receipt-subtotal-gyd"
                                            accent
                                        />
                                        <ReceiptRow
                                            label="Duties / airport / insurance"
                                            value={fmtGyd(result.feesGyd)}
                                            testId="receipt-fees-gyd"
                                        />
                                    </div>

                                    {/* Grand total */}
                                    <div className="mt-6 border-2 border-zinc-900 bg-[#FFCC00] p-4">
                                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-900">
                                            Grand total · payable in Guyana
                                        </div>
                                        <div
                                            className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-2 text-zinc-900"
                                            data-testid="receipt-grand-total"
                                        >
                                            {fmtGyd(result.grandTotalGyd)}
                                        </div>
                                    </div>

                                    {/* Down payment · 65% */}
                                    <div className="mt-3 border-2 border-zinc-900 bg-[#16A34A] text-white p-4 shadow-brutal-sm relative overflow-hidden">
                                        <span className="absolute top-2 right-2 bg-white text-[#16A34A] text-[9px] uppercase tracking-[0.25em] font-extrabold px-2 py-0.5 border-2 border-zinc-900">
                                            Pay now
                                        </span>
                                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold opacity-90">
                                            Down payment · 65%
                                        </div>
                                        <div
                                            className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-2"
                                            data-testid="receipt-down-payment"
                                        >
                                            {fmtGyd(result.downPaymentGyd)}
                                        </div>
                                        <div className="mt-2 flex items-center justify-between text-[11px] uppercase tracking-[0.2em] font-bold opacity-90">
                                            <span>Balance on delivery</span>
                                            <span
                                                className="font-mono-tab"
                                                data-testid="receipt-balance"
                                            >
                                                {fmtGyd(result.balanceGyd)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <p className="mt-3 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500 text-center">
                                ※ Estimate only · Rates may vary by carrier
                            </p>
                        </div>
                    </Reveal>
                    </div>
                </div>
            </section>

            {/* TRACK SECTION */}
            <section id="track" className="border-b-2 border-zinc-900 bg-white">
                <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
                    <ChapterTag n="02" label="Tracking Portal" />
                    <Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
                        <div className="md:col-span-5">
                            <div className="font-display text-7xl sm:text-8xl font-extrabold leading-none text-outline">
                                02
                            </div>
                            <div className="mt-3 text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                                Tracking portal
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-2">
                                Where&apos;s my parcel?
                            </h2>
                            <p className="mt-4 text-zinc-600 leading-relaxed">
                                Enter any tracking number from USPS, UPS, FedEx, DHL, Amazon,
                                China Post, Royal Mail and 700+ other carriers. We auto-detect
                                the carrier and pull the latest status with full event history.
                            </p>
                            <p className="mt-2 text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-400">
                                Results cached 5 minutes · powered by 17TRACK
                            </p>
                        </div>
                        <div className="md:col-span-7">
                            <div className="border-2 border-zinc-900 bg-white shadow-brutal p-6">
                                <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-2">
                                    Tracking number
                                </label>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <div className="flex-1 relative">
                                        <input
                                            data-testid="track-input"
                                            type="text"
                                            value={trackNum}
                                            onChange={(e) => setTrackNum(e.target.value)}
                                            placeholder="e.g. 9400111202555842761729"
                                            className="w-full bg-white border-2 border-zinc-900 px-4 py-4 pr-10 text-lg font-mono-tab font-bold text-zinc-900 focus:outline-none focus:shadow-brutal-sm rounded-none placeholder:text-zinc-300"
                                            disabled={trackLoading}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleTrack();
                                                }
                                            }}
                                        />
                                        {trackNum && !trackLoading && (
                                            <button
                                                type="button"
                                                onClick={clearTrack}
                                                aria-label="Clear"
                                                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center border-2 border-zinc-900 bg-white hover:bg-zinc-900 hover:text-[#FFCC00] transition-colors"
                                            >
                                                <X size={11} weight="bold" />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        data-testid="track-btn"
                                        onClick={handleTrack}
                                        disabled={trackLoading}
                                        className="border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                                    >
                                        {trackLoading ? (
                                            <>
                                                <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                Tracking…
                                            </>
                                        ) : (
                                            <>
                                                <MagnifyingGlass size={14} weight="bold" />
                                                Track
                                            </>
                                        )}
                                    </button>
                                </div>
                                {trackError && (
                                    <div
                                        data-testid="track-error"
                                        className="mt-3 border-2 border-zinc-900 bg-white px-3 py-2 text-xs font-bold text-zinc-900"
                                    >
                                        {trackError}
                                    </div>
                                )}
                                {needsZip && (
                                    <div className="mt-3">
                                        <label className="block text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500 mb-2">
                                            Destination ZIP / Postal Code{" "}
                                            <span className="text-zinc-400">
                                                · required for Amazon
                                            </span>
                                        </label>
                                        <input
                                            data-testid="track-zip"
                                            type="text"
                                            value={trackZip}
                                            onChange={(e) => setTrackZip(e.target.value)}
                                            placeholder="e.g. 33101"
                                            className="w-full sm:max-w-[200px] bg-white border-2 border-zinc-900 px-4 py-3 text-base font-mono-tab font-bold text-zinc-900 focus:outline-none focus:shadow-brutal-sm rounded-none placeholder:text-zinc-300"
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleTrack();
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    </Reveal>

                    {/* Results */}
                    {trackResult && (
                        <div
                            id="track-results"
                            data-testid="track-results"
                            className="grid grid-cols-1 md:grid-cols-12 gap-6"
                        >
                            {/* Status card */}
                            <div className="md:col-span-5">
                                <div className="border-2 border-zinc-900 bg-[#FFCC00] p-6 shadow-brutal">
                                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-900">
                                        Current status
                                    </div>
                                    <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-2 leading-none">
                                        {trackResult.status || "—"}
                                    </div>
                                    {trackResult.sub_status && (
                                        <div className="mt-1 text-xs font-bold uppercase tracking-widest">
                                            {trackResult.sub_status.replace(/_/g, " ")}
                                        </div>
                                    )}
                                    {trackResult.latest_event_info && (
                                        <div className="mt-4 text-sm leading-relaxed text-zinc-900">
                                            {trackResult.latest_event_info}
                                        </div>
                                    )}
                                    <div className="mt-4 grid grid-cols-2 gap-3 text-[10px] uppercase tracking-[0.2em] font-bold">
                                        <div className="border-2 border-zinc-900 bg-white p-2">
                                            <div className="text-zinc-500">Carrier</div>
                                            <div
                                                className="text-zinc-900 normal-case tracking-normal mt-1 font-mono-tab font-bold text-sm"
                                                data-testid="track-carrier"
                                            >
                                                {trackResult.carrier_name || "Auto-detecting…"}
                                            </div>
                                        </div>
                                        <div className="border-2 border-zinc-900 bg-white p-2">
                                            <div className="text-zinc-500">Tracking #</div>
                                            <div className="text-zinc-900 normal-case tracking-normal mt-1 font-mono-tab text-sm break-all">
                                                {trackResult.tracking_number}
                                            </div>
                                        </div>
                                    </div>
                                    {trackResult.cached && (
                                        <div className="mt-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] font-bold text-zinc-700">
                                            <Clock size={11} weight="bold" /> Cached · refresh in
                                            5 min
                                        </div>
                                    )}
                                    {(trackResult.carrier_code === "100308" ||
                                        /^TB[AM]/i.test(trackResult.tracking_number)) && (
                                        <a
                                            href={`https://track.amazon.com/tracking/${trackResult.tracking_number}${
                                                trackZip ? `?destZip=${encodeURIComponent(trackZip)}` : ""
                                            }`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="open-on-amazon"
                                            className="mt-4 inline-flex items-center gap-2 border-2 border-zinc-900 bg-white px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-zinc-900 hover:text-[#FFCC00] transition-colors"
                                        >
                                            <LinkIcon size={12} weight="bold" />
                                            Open on Amazon →
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Timeline */}
                            <div className="md:col-span-7">
                                <div className="border-2 border-zinc-900 bg-white p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-900">
                                            Event history
                                        </div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold">
                                            {trackResult.events?.length || 0} event
                                            {(trackResult.events?.length || 0) === 1 ? "" : "s"}
                                        </div>
                                    </div>
                                    {!trackResult.events?.length ? (
                                        <div className="border-2 border-dashed border-zinc-300 px-4 py-12 text-center text-sm text-zinc-500">
                                            <WarningCircle
                                                size={28}
                                                weight="bold"
                                                className="mx-auto mb-2 text-zinc-400"
                                            />
                                            No events yet. New tracking numbers can take a few
                                            minutes to appear.
                                        </div>
                                    ) : (
                                        <ol className="relative border-l-2 border-zinc-900 ml-3 space-y-5">
                                            {trackResult.events.map((ev, i) => (
                                                <li
                                                    key={i}
                                                    className="ml-5 relative"
                                                    data-testid={`track-event-${i}`}
                                                >
                                                    <span
                                                        className={`absolute -left-[34px] top-0 w-5 h-5 flex items-center justify-center border-2 border-zinc-900 ${
                                                            i === 0
                                                                ? "bg-[#FFCC00]"
                                                                : "bg-white"
                                                        }`}
                                                    >
                                                        {i === 0 ? (
                                                            <CheckCircle
                                                                size={10}
                                                                weight="fill"
                                                            />
                                                        ) : (
                                                            <span className="w-1.5 h-1.5 bg-zinc-900" />
                                                        )}
                                                    </span>
                                                    <div className="text-sm font-bold text-zinc-900">
                                                        {ev.description || "—"}
                                                    </div>
                                                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                                                        {ev.time && (
                                                            <span className="inline-flex items-center gap-1">
                                                                <Clock size={11} weight="bold" />
                                                                {new Date(ev.time).toLocaleString()}
                                                            </span>
                                                        )}
                                                        {ev.location && (
                                                            <span className="inline-flex items-center gap-1">
                                                                <MapPin size={11} weight="bold" />
                                                                {ev.location}
                                                            </span>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* QUICK-SCAN TOOLS */}
            <section id="bookmarklet" className="border-b-2 border-zinc-900 bg-white">
                <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
                    <ChapterTag n="03" label="Power Tools" />
                    <Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                        <div className="md:col-span-5">
                            <div className="font-display text-7xl sm:text-8xl font-extrabold leading-none text-outline">
                                03
                            </div>
                            <div className="mt-3 text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                                Power tools
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-2">
                                Scan from any store.
                            </h2>
                            <p className="mt-4 text-zinc-600 leading-relaxed">
                                One-tap price + weight scans from Amazon, eBay, Walmart,
                                Shein, AliExpress and thousands of other stores. Choose your
                                setup:
                            </p>
                        </div>
                        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Bookmarklet */}
                            <div className="border-2 border-zinc-900 bg-[#FFCC00] p-5 shadow-brutal-sm">
                                <div className="text-[10px] uppercase tracking-[0.25em] font-bold">
                                    Option A · Bookmarklet
                                </div>
                                <div className="font-display text-2xl font-extrabold tracking-tight mt-1">
                                    Drag &amp; drop
                                </div>
                                <p className="text-xs text-zinc-800 mt-2 leading-relaxed">
                                    Drag the yellow button below to your bookmarks bar.
                                    Tap it on any product page to auto-scan.
                                </p>
                                <a
                                    href={`javascript:void(window.open('https://quotematrix.online/?scan='+encodeURIComponent(location.href),'_blank'))`}
                                    data-testid="bookmarklet-link"
                                    onClick={(e) => e.preventDefault()}
                                    draggable="true"
                                    className="mt-4 inline-flex items-center gap-2 border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.2em] cursor-grab active:cursor-grabbing hover:bg-white hover:text-zinc-900 transition-colors"
                                >
                                    <MagnifyingGlass size={14} weight="bold" />
                                    Send to QuoteMatrix
                                </a>
                                <div className="mt-3 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700">
                                    Works on desktop &amp; mobile
                                </div>
                            </div>

                            {/* Chrome extension */}
                            <div className="border-2 border-zinc-900 bg-white p-5 shadow-brutal-sm">
                                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                                    Option B · Chrome extension
                                </div>
                                <div className="font-display text-2xl font-extrabold tracking-tight mt-1">
                                    Install once
                                </div>
                                <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
                                    Adds a floating <b>&quot;Send to QuoteMatrix&quot;</b> button on
                                    Amazon, eBay, Walmart, Shein &amp; AliExpress product pages.
                                </p>
                                <a
                                    href="/quotematrix-extension.zip"
                                    download
                                    data-testid="extension-download"
                                    className="mt-4 inline-flex items-center gap-2 border-2 border-zinc-900 bg-[#FFCC00] text-zinc-900 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.2em] hover:-translate-y-0.5 hover:shadow-brutal-sm transition-all"
                                >
                                    <ArrowRight size={14} weight="bold" />
                                    Download extension
                                </a>
                                <div className="mt-3 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                                    Chrome / Edge / Brave
                                </div>
                            </div>
                        </div>
                    </div>
                    </Reveal>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="border-b-2 border-zinc-900 bg-white">
                <div className="max-w-7xl mx-auto px-6 py-16 md:py-24">
                    <ChapterTag n="04" label="The Manifesto" />
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
                        <div className="md:col-span-4">
                            <div className="font-display text-7xl sm:text-8xl font-extrabold leading-none text-outline">
                                04
                            </div>
                            <div className="mt-3 text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                                Methodology
                            </div>
                            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-2">
                                How the math works.
                            </h2>
                        </div>
                        <div className="md:col-span-8 text-zinc-600 text-base leading-relaxed">
                            Every shipment passes through four checkpoints: the purchase, the
                            US sales tax, the converted GYD value plus tiered shipping, and the
                            final Guyana-side duties, airport handling and insurance. Each
                            checkpoint is transparent and re-calculated as you type.
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[
                            {
                                n: "01",
                                t: "Enter USD",
                                d: "Your purchase amount on the US side. Decimal allowed.",
                            },
                            {
                                n: "02",
                                t: "Apply sales tax",
                                d: "Default 7% of USD — adjustable per state.",
                            },
                            {
                                n: "03",
                                t: "Convert + ship",
                                d: "Add tiered shipping by pound.",
                            },
                            {
                                n: "04",
                                t: "Add fees",
                                d: "Duties, airport transport and insurance on the subtotal.",
                            },
                        ].map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.08}>
                            <div
                                className="h-full border-2 border-zinc-900 bg-[#F8FAFC] p-6 hover:bg-[#FFCC00] hover:-translate-y-1 hover:shadow-brutal-sm transition-all duration-300"
                            >
                                <div className="font-mono-tab text-xs font-bold text-zinc-500">
                                    STEP / {s.n}
                                </div>
                                <div className="font-display text-2xl font-extrabold tracking-tight mt-2">
                                    {s.t}
                                </div>
                                <div className="text-sm text-zinc-700 mt-2">{s.d}</div>
                            </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-zinc-900 text-white">
                <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#FFCC00] flex items-center justify-center">
                            <Package size={22} weight="bold" color="#09090B" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-display text-base font-extrabold tracking-tight">
                                CARGO/USD→GYD
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                                A pragmatic freight calculator
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.2em] text-zinc-400 flex-wrap">
                        <a
                            href="/privacy-policy"
                            data-testid="footer-privacy-link"
                            className="hover:text-[#FFCC00] transition-colors"
                        >
                            Privacy policy
                        </a>
                        <a
                            href="/admin"
                            data-testid="footer-admin-link"
                            className="inline-flex items-center gap-1.5 hover:text-[#FFCC00] transition-colors"
                        >
                            <Lock size={11} weight="bold" />
                            Admin login
                        </a>
                        <span>
                            © {new Date().getFullYear()} — Built for shippers, not spreadsheets.
                        </span>
                    </div>
                </div>
            </footer>

            {/* Floating action buttons */}
            <div className="fixed bottom-20 right-6 z-50 flex flex-col items-end gap-3">
                <button
                    type="button"
                    data-testid="save-quote-btn"
                    onClick={handleSaveQuote}
                    aria-label="Save quote and copy link"
                    className="group flex items-center gap-2 bg-white text-zinc-900 border-2 border-zinc-900 shadow-brutal-sm px-4 py-3 hover:-translate-y-0.5 hover:shadow-brutal hover:bg-[#FFCC00] transition-all"
                >
                    <BookmarkSimple size={22} weight="fill" />
                    <span className="hidden sm:flex flex-col items-start leading-tight">
                        <span className="text-[9px] uppercase tracking-[0.25em] font-bold opacity-90">
                            {savedQuoteId ? "Quote saved" : "Save & share"}
                        </span>
                        <span className="font-mono-tab text-xs font-bold">
                            {savedQuoteId ? `#${savedQuoteId}` : "copy link"}
                        </span>
                    </span>
                </button>

                <button
                    type="button"
                    data-testid="whatsapp-btn"
                    onClick={handleWhatsApp}
                    aria-label="Send quote to WhatsApp"
                    className="group flex items-center gap-2 bg-[#25D366] text-white border-2 border-zinc-900 shadow-brutal px-4 py-3 hover:-translate-y-0.5 hover:shadow-[8px_8px_0px_0px_#09090B] transition-all"
                >
                    <WhatsappLogo size={28} weight="fill" />
                    <span className="hidden sm:flex flex-col items-start leading-tight">
                        <span className="text-[9px] uppercase tracking-[0.25em] font-bold opacity-90">
                            Send quote
                        </span>
                        <span className="font-mono-tab text-xs font-bold">
                            592-666-5489
                        </span>
                    </span>
                </button>
            </div>

            {/* Image scan review modal */}
            {imgReview && (
                <div
                    className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/60 overflow-auto"
                    data-lenis-prevent
                    onClick={() => setImgReview(null)}
                    data-testid="img-review-modal"
                >
                    <div
                        className="w-full max-w-2xl border-2 border-zinc-900 bg-white shadow-brutal my-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-zinc-900 text-white px-5 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <PencilSimple size={16} weight="bold" color="#FFCC00" />
                                <span className="text-[11px] uppercase tracking-[0.25em] font-extrabold">
                                    Review extracted details
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setImgReview(null)}
                                className="w-6 h-6 flex items-center justify-center border-2 border-white bg-transparent hover:bg-white hover:text-zinc-900"
                                aria-label="Close"
                            >
                                <X size={11} weight="bold" />
                            </button>
                        </div>

                        <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4">
                            {imgPreview && (
                                <div className="md:col-span-4">
                                    <button
                                        type="button"
                                        onClick={() => setLightboxSrc(imgPreview)}
                                        data-testid="review-image-preview"
                                        aria-label="Enlarge uploaded screenshot"
                                        title="Tap to enlarge"
                                        className="relative group w-full border-2 border-zinc-900 bg-zinc-50 cursor-zoom-in overflow-hidden"
                                    >
                                        <img
                                            src={imgPreview}
                                            alt="Uploaded"
                                            className="w-full max-h-64 object-contain"
                                        />
                                        <span className="absolute inset-0 flex items-center justify-center bg-zinc-900/0 text-transparent group-hover:bg-zinc-900/20 group-hover:text-[#FFCC00] transition-colors">
                                            <Plus size={26} weight="bold" />
                                        </span>
                                    </button>
                                </div>
                            )}
                            <div className={imgPreview ? "md:col-span-8" : "md:col-span-12"}>
                                {imgReview.note && (
                                    <div className="mb-3 border-2 border-dashed border-zinc-400 bg-[#FFFDF2] px-3 py-2 text-xs font-bold text-zinc-800">
                                        {imgReview.note}
                                    </div>
                                )}

                                {imgReview.variants?.length > 0 && (
                                    <div className="mb-4">
                                        <div className={labelCls}>Select a variant</div>
                                        <div className="space-y-1">
                                            {imgReview.variants.map((v, i) => (
                                                <label
                                                    key={i}
                                                    className={`flex items-center gap-2 border-2 border-zinc-900 px-3 py-2 cursor-pointer ${
                                                        imgReview.selected_variant === v.label
                                                            ? "bg-[#FFCC00]"
                                                            : "bg-white hover:bg-[#FFCC00]/40"
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="variant"
                                                        checked={imgReview.selected_variant === v.label}
                                                        onChange={() =>
                                                            setImgReview({
                                                                ...imgReview,
                                                                selected_variant: v.label,
                                                                edit_item_price: v.price ?? imgReview.edit_item_price,
                                                                edit_currency: v.currency ?? imgReview.edit_currency,
                                                            })
                                                        }
                                                    />
                                                    <span className="flex-1 text-sm font-bold">{v.label}</span>
                                                    <span className="font-mono-tab text-sm">
                                                        {v.currency} {Number(v.price || 0).toFixed(2)}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {/* ORDER TOTAL — shown when a checkout/order summary total was detected */}
                                    <div className="sm:col-span-2 border-2 border-zinc-900 bg-[#FFCC00]/40 p-3">
                                        <div className={labelCls}>
                                            <span className="inline-flex items-center gap-2">
                                                <Receipt size={12} weight="bold" />
                                                Order total · if shown on checkout (includes item + shipping + tax)
                                            </span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            data-testid="review-order-total"
                                            value={imgReview.edit_order_total}
                                            onChange={(e) =>
                                                setImgReview({ ...imgReview, edit_order_total: e.target.value })
                                            }
                                            placeholder="Optional — overrides item + shipping below"
                                            className="w-full bg-white border-2 border-zinc-900 px-3 py-2 text-lg font-mono-tab font-bold"
                                        />
                                        <div className="mt-1 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
                                            When filled, this exact figure is quoted and US sales tax is set to 0%
                                        </div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <div className={labelCls}>Product title</div>
                                        <input
                                            type="text"
                                            data-testid="review-title"
                                            value={imgReview.edit_title}
                                            onChange={(e) =>
                                                setImgReview({ ...imgReview, edit_title: e.target.value })
                                            }
                                            className="w-full bg-white border-2 border-zinc-900 px-3 py-2 text-sm font-bold"
                                        />
                                    </div>
                                    <div>
                                        <div className={labelCls}>Item price</div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            data-testid="review-item-price"
                                            value={imgReview.edit_item_price}
                                            onChange={(e) =>
                                                setImgReview({ ...imgReview, edit_item_price: e.target.value })
                                            }
                                            className="w-full bg-white border-2 border-zinc-900 px-3 py-2 text-sm font-mono-tab"
                                        />
                                    </div>
                                    <div>
                                        <div className={labelCls}>Seller shipping</div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            data-testid="review-seller-shipping"
                                            value={imgReview.edit_seller_shipping}
                                            onChange={(e) =>
                                                setImgReview({
                                                    ...imgReview,
                                                    edit_seller_shipping: e.target.value,
                                                })
                                            }
                                            className="w-full bg-white border-2 border-zinc-900 px-3 py-2 text-sm font-mono-tab"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div>
                                        <div className={labelCls}>
                                            US sales tax %
                                            <span className="ml-2 normal-case text-zinc-500">
                                                · {imgReview.order_total != null && imgReview.edit_order_total !== ""
                                                    ? "inside order total"
                                                    : imgReview.sales_tax != null || imgReview.sales_tax_percent != null
                                                    ? "from screenshot"
                                                    : "assumed 7%"}
                                            </span>
                                        </div>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            data-testid="review-tax-percent"
                                            value={imgReview.edit_tax_percent}
                                            onChange={(e) =>
                                                setImgReview({
                                                    ...imgReview,
                                                    edit_tax_percent: e.target.value,
                                                })
                                            }
                                            className="w-full bg-white border-2 border-zinc-900 px-3 py-2 text-sm font-mono-tab"
                                        />
                                    </div>
                                    <div>
                                        <div className={labelCls}>Currency</div>
                                        <select
                                            data-testid="review-currency"
                                            value={imgReview.edit_currency}
                                            onChange={(e) =>
                                                setImgReview({
                                                    ...imgReview,
                                                    edit_currency: e.target.value,
                                                })
                                            }
                                            className="w-full bg-white border-2 border-zinc-900 px-3 py-2 text-sm font-mono-tab"
                                        >
                                            {["USD", "GBP", "EUR", "CAD", "AUD", "JPY", "INR", "MXN", "BRL", "CNY"].map(
                                                (c) => (
                                                    <option key={c} value={c}>{c}</option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                    <div>
                                        <div className={labelCls}>
                                            Weight (lb)
                                            {imgReview.weight_source && (
                                                <span className="ml-2 normal-case text-zinc-500">
                                                    · {imgReview.weight_source === "web" ? "found online" : imgReview.weight_source === "screenshot" ? "from screenshot" : "ai estimate"}
                                                    {imgReview.weight_label ? ` (${imgReview.weight_label})` : ""}
                                                </span>
                                            )}
                                        </div>
                                        <input
                                            type="number"
                                            step="0.1"
                                            data-testid="review-weight"
                                            value={imgReview.edit_weight_pounds}
                                            onChange={(e) =>
                                                setImgReview({
                                                    ...imgReview,
                                                    edit_weight_pounds: e.target.value,
                                                })
                                            }
                                            placeholder="Enter or estimate"
                                            className="w-full bg-white border-2 border-zinc-900 px-3 py-2 text-sm font-mono-tab"
                                        />
                                        {imgReview.weight_source_url && (
                                            <a
                                                href={imgReview.weight_source_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-1 inline-block text-[10px] uppercase tracking-[0.15em] font-bold text-blue-700 hover:underline break-all"
                                            >
                                                Source ↗
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {imgReview.ambiguities?.length > 0 && (
                                    <div className="mt-4 border-2 border-dashed border-zinc-400 bg-white p-3 text-xs">
                                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">
                                            Uncertainties
                                        </div>
                                        <ul className="list-disc pl-4 space-y-0.5 text-zinc-700">
                                            {imgReview.ambiguities.map((a, i) => (
                                                <li key={i}>{a}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="mt-5 flex flex-col sm:flex-row gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setImgReview(null)}
                                        className="flex-1 border-2 border-zinc-900 bg-white text-zinc-900 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-zinc-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="review-confirm"
                                        onClick={applyImgReview}
                                        className="flex-1 border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors"
                                    >
                                        Apply to calculator
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Full-screen image lightbox */}
            {lightboxSrc && (
                <ImageLightbox
                    src={lightboxSrc}
                    onClose={() => setLightboxSrc(null)}
                />
            )}

            {/* Giveaway modal */}
            {giveaway && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
                    data-lenis-prevent
                    onClick={() => setGiveaway(null)}
                    data-testid="giveaway-modal"
                >
                    <div
                        className="w-full max-w-lg border-2 border-zinc-900 bg-white shadow-brutal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-[#FFCC00] border-b-2 border-zinc-900 px-6 py-3 flex items-center gap-2 justify-between">
                            <div className="flex items-center gap-2">
                                <Gift size={18} weight="fill" />
                                <span className="text-[11px] uppercase tracking-[0.25em] font-extrabold">
                                    You&apos;re in!
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setGiveaway(null)}
                                className="w-6 h-6 flex items-center justify-center border-2 border-zinc-900 bg-white hover:bg-zinc-900 hover:text-[#FFCC00]"
                                aria-label="Close"
                            >
                                <X size={11} weight="bold" />
                            </button>
                        </div>

                        <div className="p-6">
                            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-500">
                                Entry confirmed
                            </div>
                            <h3 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">
                                Thank you for your entry.
                            </h3>
                            <p className="mt-4 text-base text-zinc-700 leading-relaxed">
                                Our WhatsApp team will reach out to you shortly at{" "}
                                <span className="font-mono-tab font-bold">
                                    +{giveaway.phone}
                                </span>
                                .
                            </p>

                            <button
                                type="button"
                                onClick={() => setGiveaway(null)}
                                data-testid="giveaway-close-btn"
                                className="mt-6 w-full border-2 border-zinc-900 bg-zinc-900 text-[#FFCC00] px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#FFCC00] hover:text-zinc-900 transition-colors"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
